// Client-side transient animation tracker.
//
// Server snapshots only describe live state — a shell that hits a tank
// simply disappears from the next snapshot, and a killed tank's `alive`
// flag flips to false. The renderer wants to *play an animation* at
// each of those moments, so we diff consecutive snapshots and spawn
// short-lived "explosion" particles client-side.
//
// Particles live entirely on the client. They're not authoritative; if
// snapshot delivery hiccups we'll lose an explosion, which is fine — it
// was cosmetic anyway.

import {
  TANK_BIG_EXPLOSION_THRESHOLD,
  TANK_DIRECTIONS,
  TILE_SIZE,
  type MergedWorldSnapshot,
} from "@webolo/shared";

export interface Explosion {
  x: number;
  y: number;
  startedAt: number; // performance.now() at spawn
  // Bigger explosions for tank deaths so a kill reads visually distinct
  // from a shell-on-wall thwack.
  size: "small" | "large";
}

const EXPLOSION_FRAMES = 8;
const FRAME_MS = 60;
export const EXPLOSION_DURATION_MS = EXPLOSION_FRAMES * FRAME_MS;

// Compass-step unit vectors. Step 0 = north, rotating CW. Matches the
// server's DIR_DX/DIR_DY so death-explosion geometry lines up.
const DIR_DX: number[] = new Array(TANK_DIRECTIONS);
const DIR_DY: number[] = new Array(TANK_DIRECTIONS);
for (let i = 0; i < TANK_DIRECTIONS; i++) {
  const a = (i / TANK_DIRECTIONS) * Math.PI * 2 - Math.PI / 2;
  DIR_DX[i] = Math.cos(a);
  DIR_DY[i] = Math.sin(a);
}

export class ExplosionTracker {
  private prevAlive = new Map<number, boolean>();
  // Per-pillbox "was it already destroyed?" so we bang once on the
  // standing→dropped transition (shelled down, or swallowed by battle-royale
  // fog). Only updated on snapshots that actually carry the pillbox list.
  private prevPillDropped = new Map<number, boolean>();
  private explosions: Explosion[] = [];

  // Diffs the new snapshot against the previous one and spawns
  // explosions for shells that disappeared and tanks that just died.
  ingest(prev: MergedWorldSnapshot | null, curr: MergedWorldSnapshot): void {
    const now = performance.now();
    if (prev) {
      // Shell removed -> impact at last known position.
      const currIds = new Set(curr.shells.map((s) => s.id));
      for (const ps of prev.shells) {
        if (!currIds.has(ps.id)) {
          // Shell with TTL expiring at end-of-life shouldn't bang —
          // we can't easily know "expired" vs "hit something" without
          // more protocol. Spawning either way is fine; a shell
          // expiring on grass will just play a small puff there which
          // is mild and acceptable.
          this.explosions.push({ x: ps.x, y: ps.y, startedAt: now, size: "small" });
        }
      }
      // Tank alive -> dead transition: replicate tankexp.c's death
      // explosion. Small (low ammo) plays a single large sprite at
      // the tank's death tile. Big (shells + mines > threshold) plays
      // four sprites in a 2x2 block extending one tile in the
      // direction the tank was facing — same geometry the server
      // uses for craters.
      const prevById = new Map(prev.players.map((p) => [p.id, p]));
      for (const cp of curr.players) {
        const wasAlive = this.prevAlive.get(cp.id);
        if (wasAlive !== true || cp.alive) continue;
        const pp = prevById.get(cp.id);
        const ammoAtDeath = pp ? pp.shells + pp.mines : 0;
        const big = ammoAtDeath > TANK_BIG_EXPLOSION_THRESHOLD;
        const dir = pp ? pp.dir : cp.dir;
        this.spawnDeathExplosion(cp.x, cp.y, dir, big, now);
      }
    }
    // Pillbox standing→destroyed → big explosion at its tile. pillboxes is
    // delta-encoded, so only diff/track on snapshots that include the list.
    if (curr.pillboxes) {
      for (const pb of curr.pillboxes) {
        const wasDropped = this.prevPillDropped.get(pb.id);
        if (wasDropped === false && pb.dropped) {
          const cx = pb.tileX * TILE_SIZE + TILE_SIZE / 2;
          const cy = pb.tileY * TILE_SIZE + TILE_SIZE / 2;
          this.explosions.push({ x: cx, y: cy, startedAt: now, size: "large" });
        }
      }
      this.prevPillDropped.clear();
      for (const pb of curr.pillboxes) this.prevPillDropped.set(pb.id, pb.dropped);
    }

    // Update tracking state.
    for (const p of curr.players) this.prevAlive.set(p.id, p.alive);
    // Forget players who left the room so we don't leak state.
    const live = new Set(curr.players.map((p) => p.id));
    for (const id of [...this.prevAlive.keys()]) {
      if (!live.has(id)) this.prevAlive.delete(id);
    }
  }

  // Mirrors physics.ts spawnDeathExplosion: small = 1 big sprite at the
  // death position; big = 4 sprites in a 2x2 block offset by ONE tile in
  // the tank's facing direction. Each axis snaps to +-1 just like
  // tankexp.c:459-468 (zero -> -1).
  private spawnDeathExplosion(x: number, y: number, dir: number, big: boolean, now: number): void {
    if (!big) {
      this.explosions.push({ x, y, startedAt: now, size: "large" });
      return;
    }
    const dx = DIR_DX[dir] ?? 0;
    const dy = DIR_DY[dir] ?? 0;
    const nx = dx > 0 ? 1 : -1;
    const ny = dy > 0 ? 1 : -1;
    const cx = Math.floor(x / TILE_SIZE) * TILE_SIZE + TILE_SIZE / 2;
    const cy = Math.floor(y / TILE_SIZE) * TILE_SIZE + TILE_SIZE / 2;
    const offsets: Array<[number, number, number]> = [
      [0,                0,              0],
      [nx * TILE_SIZE,   0,              40],
      [0,                ny * TILE_SIZE, 80],
      [nx * TILE_SIZE,   ny * TILE_SIZE, 120],
    ];
    for (const [ox, oy, delay] of offsets) {
      this.explosions.push({
        x: cx + ox,
        y: cy + oy,
        startedAt: now + delay, // stagger so the 4 frames feel like a sweep
        size: "large",
      });
    }
  }

  // Returns currently-active explosions and prunes finished ones.
  active(now: number): Explosion[] {
    const cutoff = now - EXPLOSION_DURATION_MS;
    this.explosions = this.explosions.filter((e) => e.startedAt > cutoff);
    return this.explosions;
  }

  // Frame index 0..EXPLOSION_FRAMES-1 for an explosion at the given
  // current time. Returns -1 if the animation is finished.
  static frameIndex(e: Explosion, now: number): number {
    const dt = now - e.startedAt;
    const idx = Math.floor(dt / FRAME_MS);
    return idx < EXPLOSION_FRAMES ? idx : -1;
  }
}
